import { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import {
    Beaker,
    Play,
    RefreshCw,
    CheckCircle,
    AlertCircle,
    Database,
    Users,
    Package,
    DollarSign,
    Target,
    Globe
} from 'lucide-react';

const STANDARD_EVENTS = [
    'purchase', 'add_to_cart', 'click_link', 'view_content',
    'select_payment_method', 'payment_failed', 'upsell',
    'finished_quiz', 'started_quiz', 'clicked_lp'
];

interface SimulationResult {
    success: boolean;
    simulation: boolean;
    test_click_id: string;
    input: {
        affiliate_id: string;
        affiliate_email: string;
        product_id: string;
        product_name: string;
        sale_amount: number;
    };
    commission_calculation: {
        source: string;
        rate_used: string;
        user_override_exists: boolean;
        user_override_rate: string;
        product_default_rate: string;
        calculated_commission: string;
    };
    message: string;
    note: string;
}

interface User {
    id: string;
    email: string;
    company_details?: { name: string };
    overrides: Record<string, number>;
}

interface Product {
    id: string;
    name: string;
    default_commission: number;
}

export default function AdminSandbox() {
    const [users, setUsers] = useState<User[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [simulating, setSimulating] = useState(false);
    const [creatingTestData, setCreatingTestData] = useState(false);

    const [selectedUser, setSelectedUser] = useState('');
    const [selectedProduct, setSelectedProduct] = useState('');
    const [saleAmount, setSaleAmount] = useState('100');
    const [selectedEvent, setSelectedEvent] = useState('purchase');
    const [selectedCountry, setSelectedCountry] = useState('');

    const [result, setResult] = useState<SimulationResult | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch affiliates
            const usersQuery = query(collection(db, 'users'), where('role', '==', 'affiliate'));
            const usersSnapshot = await getDocs(usersQuery);
            const usersList = usersSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as User[];
            setUsers(usersList);

            // Fetch products
            const productsSnapshot = await getDocs(collection(db, 'products'));
            const productsList = productsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Product[];
            setProducts(productsList);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTestData = async () => {
        setCreatingTestData(true);
        setError('');
        try {
            const createTestDataFunc = httpsCallable(functions, 'createTestData');
            const result = await createTestDataFunc({});
            console.log('Test data created:', result.data);
            alert('Test data created successfully! Refresh the page to see new affiliates and products.');
            fetchData();
        } catch (err: any) {
            console.error('Error creating test data:', err);
            setError('Failed to create test data: ' + err.message);
        } finally {
            setCreatingTestData(false);
        }
    };

    const handleSimulate = async () => {
        if (!selectedUser || !selectedProduct || !saleAmount) {
            setError('Please select an affiliate, product, and enter a sale amount');
            return;
        }

        setSimulating(true);
        setError('');
        setResult(null);

        try {
            const simulateFunc = httpsCallable(functions, 'simulatePostback');
            const response = await simulateFunc({
                affiliate_id: selectedUser,
                product_id: selectedProduct,
                amount: parseFloat(saleAmount),
                event_type: selectedEvent,
                country: selectedCountry || undefined,
            });

            setResult(response.data as SimulationResult);
        } catch (err: any) {
            console.error('Simulation error:', err);
            setError('Simulation failed: ' + (err.message || 'Unknown error'));
        } finally {
            setSimulating(false);
        }
    };

    const selectedUserData = users.find(u => u.id === selectedUser);

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white">Test Sandbox</h1>
                    <p className="text-gray-400">Simulate postbacks to verify commission logic</p>
                </div>
                <button
                    onClick={handleCreateTestData}
                    disabled={creatingTestData}
                    className="btn-secondary flex items-center gap-2"
                >
                    {creatingTestData ? (
                        <RefreshCw size={20} className="animate-spin" />
                    ) : (
                        <Database size={20} />
                    )}
                    Create Test Data
                </button>
            </div>

            {/* Info Box */}
            <div className="card bg-linear-to-r from-primary-500/10 to-accent-500/10 border-primary-500/20">
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center shrink-0">
                        <Beaker className="text-primary-400" size={24} />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-white mb-1">Commission Hierarchy Test</h3>
                        <p className="text-gray-400 text-sm">
                            This sandbox simulates a conversion without creating real data.
                            Use it to verify the 4-level commission hierarchy:
                        </p>
                        <ol className="text-sm text-gray-400 mt-2 space-y-1 list-decimal list-inside">
                            <li><span className="text-amber-400">User + Country + Event</span> (highest priority)</li>
                            <li><span className="text-amber-400">User + Event</span></li>
                            <li><span className="text-amber-400">User + Country override</span></li>
                            <li><span className="text-primary-400">User + Product override</span></li>
                            <li><span className="text-blue-400">Product + Country + Event override</span></li>
                            <li><span className="text-blue-400">Product + Event override</span></li>
                            <li><span className="text-blue-400">Product + Country override</span></li>
                            <li><span className="text-gray-400">Product Default Rate</span> (lowest priority)</li>
                        </ol>
                    </div>
                </div>
            </div>

            {/* Simulation Form */}
            <div className="card">
                <h2 className="text-lg font-semibold text-white mb-4">Simulate Conversion</h2>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    <Users size={16} className="inline mr-2" />
                                    Select Affiliate
                                </label>
                                <select
                                    value={selectedUser}
                                    onChange={(e) => setSelectedUser(e.target.value)}
                                    className="input-field"
                                >
                                    <option value="">Choose an affiliate...</option>
                                    {users.map(user => (
                                        <option key={user.id} value={user.id}>
                                            {user.company_details?.name || user.email}
                                            {Object.keys(user.overrides || {}).length > 0 && ' ⭐'}
                                        </option>
                                    ))}
                                </select>
                                {selectedUserData && Object.keys(selectedUserData.overrides || {}).length > 0 && (
                                    <p className="text-xs text-amber-400 mt-1">
                                        Has {Object.keys(selectedUserData.overrides).length} override(s)
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    <Package size={16} className="inline mr-2" />
                                    Select Product
                                </label>
                                <select
                                    value={selectedProduct}
                                    onChange={(e) => setSelectedProduct(e.target.value)}
                                    className="input-field"
                                >
                                    <option value="">Choose a product...</option>
                                    {products.map(product => (
                                        <option key={product.id} value={product.id}>
                                            {product.name} ({product.default_commission}% default)
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    <DollarSign size={16} className="inline mr-2" />
                                    Sale Amount (€)
                                </label>
                                <input
                                    type="number"
                                    value={saleAmount}
                                    onChange={(e) => setSaleAmount(e.target.value)}
                                    placeholder="100"
                                    min="1"
                                    className="input-field"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    <Target size={16} className="inline mr-2" />
                                    Event Type
                                </label>
                                <select
                                    value={selectedEvent}
                                    onChange={(e) => setSelectedEvent(e.target.value)}
                                    className="input-field"
                                >
                                    {STANDARD_EVENTS.map(event => (
                                        <option key={event} value={event}>
                                            {event}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    <Globe size={16} className="inline mr-2" />
                                    Country (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={selectedCountry}
                                    onChange={(e) => setSelectedCountry(e.target.value.toUpperCase())}
                                    placeholder="e.g. US, UK, DE"
                                    maxLength={2}
                                    className="input-field uppercase"
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3">
                                <AlertCircle className="text-red-400" size={20} />
                                <span className="text-red-400 text-sm">{error}</span>
                            </div>
                        )}

                        <button
                            onClick={handleSimulate}
                            disabled={simulating || !selectedUser || !selectedProduct}
                            className="btn-primary w-full flex items-center justify-center gap-2"
                        >
                            {simulating ? (
                                <>
                                    <RefreshCw size={20} className="animate-spin" />
                                    Simulating...
                                </>
                            ) : (
                                <>
                                    <Play size={20} />
                                    Simulate Postback
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>

            {/* Results */}
            {result && (
                <div className="card animate-slide-up">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                            <CheckCircle className="text-emerald-400" size={24} />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-white">Simulation Complete</h2>
                            <p className="text-sm text-gray-400">Results calculated successfully</p>
                        </div>
                    </div>

                    {/* Summary */}
                    <div className="bg-linear-to-r from-primary-500/10 to-accent-500/10 rounded-xl p-6 mb-6">
                        <p className="text-lg text-white">{result.message}</p>
                    </div>

                    {/* Details Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h3 className="text-sm font-medium text-gray-400 mb-3">Input Data</h3>
                            <div className="space-y-2">
                                <div className="flex justify-between py-2 border-b border-white/5">
                                    <span className="text-gray-400">Affiliate</span>
                                    <span className="text-white">{result.input.affiliate_email}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-white/5">
                                    <span className="text-gray-400">Product</span>
                                    <span className="text-white">{result.input.product_name}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-white/5">
                                    <span className="text-gray-400">Sale Amount</span>
                                    <span className="text-white">€{result.input.sale_amount}</span>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-sm font-medium text-gray-400 mb-3">Commission Calculation</h3>
                            <div className="space-y-2">
                                <div className="flex justify-between py-2 border-b border-white/5">
                                    <span className="text-gray-400">Source Used</span>
                                    <span className={`font-medium ${result.commission_calculation.source.includes('user')
                                        ? 'text-amber-400'
                                        : 'text-gray-400'
                                        }`}>
                                        {result.commission_calculation.source.replace(/_/g, ' ')}
                                    </span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-white/5">
                                    <span className="text-gray-400">Rate Applied</span>
                                    <span className="text-white">{result.commission_calculation.rate_used}</span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-white/5">
                                    <span className="text-gray-400">User Override Rate</span>
                                    <span className={result.commission_calculation.user_override_exists ? 'text-amber-400' : 'text-gray-500'}>
                                        {result.commission_calculation.user_override_rate}
                                    </span>
                                </div>
                                <div className="flex justify-between py-2 border-b border-white/5">
                                    <span className="text-gray-400">Product Default</span>
                                    <span className="text-gray-400">{result.commission_calculation.product_default_rate}</span>
                                </div>
                                <div className="flex justify-between py-2 bg-emerald-500/10 rounded-lg px-3">
                                    <span className="text-emerald-400 font-medium">Commission</span>
                                    <span className="text-emerald-400 font-bold text-lg">
                                        €{result.commission_calculation.calculated_commission}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Note */}
                    <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                        <p className="text-blue-400 text-sm">
                            <strong>Note:</strong> {result.note}
                        </p>
                        <p className="text-blue-400 text-sm mt-2">
                            Test Click ID: <code className="bg-blue-500/20 px-2 py-1 rounded">{result.test_click_id}</code>
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
